/**
 * Anonymous display handles, in one place because BOTH SIDES MINT THEM.
 *
 * The gateway names a player who arrived without one, and the local practice
 * driver names a player who never opened a socket at all. Those used to be two
 * different generators — `anonHandle()` on the server producing "PolarFjord417",
 * and a client-side `player-s0bu6` — which meant the name you were shown before
 * your first match was not the name anybody would ever see you under.
 *
 * Pure, so it lives in shared: no node:crypto, no DOM.
 */

const ADJECTIVES = [
  'Swift', 'Bold', 'Quiet', 'Amber', 'Polar', 'Crimson', 'Lunar', 'Iron',
  'Coral', 'Nomad', 'Solar', 'Verdant', 'Basalt', 'Cobalt', 'Dusty', 'Onyx',
] as const;

const NOUNS = [
  'Meridian', 'Compass', 'Sextant', 'Atlas', 'Isthmus', 'Fjord', 'Delta',
  'Cartographer', 'Trawler', 'Monsoon', 'Caravan', 'Steppe', 'Archipelago',
  'Cordillera', 'Escarpment', 'Tundra',
] as const;

/** The longest name a player may carry. Enforced on both sides of the wire. */
export const HANDLE_MAX_LENGTH = 24;

/** Anonymous display handle. Deliberately not derived from anything personal. */
export function anonHandle(): string {
  const a = ADJECTIVES[Math.floor(Math.random() * ADJECTIVES.length)] ?? 'Swift';
  const n = NOUNS[Math.floor(Math.random() * NOUNS.length)] ?? 'Atlas';
  const d = Math.floor(Math.random() * 900) + 100;
  return `${a}${n}${d}`;
}

/**
 * A handle as it should travel: trimmed, capped, or null if the player has not
 * chosen one.
 *
 * NULL IS A REAL ANSWER and the reason this returns it rather than a fallback.
 * "I have not picked a name" has to survive the trip to the gateway, because
 * the gateway is what assigns one — a client that substituted its own default
 * here would be sending a chosen name that nobody chose, and the server has no
 * way to tell the difference.
 */
export function normalizeHandle(raw: string | undefined | null): string | null {
  const trimmed = (raw ?? '').trim().slice(0, HANDLE_MAX_LENGTH);
  return trimmed === '' ? null : trimmed;
}
